import React, { useState, useEffect } from 'react';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import UserDashboardHeader from '../../components/UserDashboardHeader';
import { API_ENDPOINTS } from '../../config/api';

interface ChamaData {
  group_type: string;
  name: string;
  description: string;
  type: string;
  county: string;
  town: string;
  contribution_amount: string;
  contribution_frequency: string;
  target_amount: string;
  contribution_rules: string;
  max_members: string;
  is_public: boolean;
  requires_approval: boolean;
  rules: string;
  meeting_schedule: string;
  payment_method: string;
  till_number: string;
  paybill_business_number: string;
  paybill_account_number: string;
  payment_recipient_name: string;
  category: string;
}

interface Member {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
}

const CreateChama: React.FC = () => {
  const colors = getThemeColors('dark');

  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [userData, setUserData] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });
  const [chamaData, setChamaData] = useState<ChamaData>({
    group_type: '',
    name: '',
    description: '',
    type: '',
    county: '',
    town: '',
    contribution_amount: '',
    contribution_frequency: 'monthly',
    target_amount: '',
    contribution_rules: '',
    max_members: '',
    is_public: false,
    requires_approval: false,
    rules: '',
    meeting_schedule: '',
    payment_method: '',
    till_number: '',
    paybill_business_number: '',
    paybill_account_number: '',
    payment_recipient_name: '',
    category: '',
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Member[]>([]);
  const [selectedMembers, setSelectedMembers] = useState<Member[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  const [showErrors, setShowErrors] = useState(false);

  // Load current user data on component mount
  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const authToken = localStorage.getItem('authToken');
        if (!authToken) return;

        const response = await fetch(API_ENDPOINTS.USER_PROFILE, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        });

        const data = await response.json();
        if (data.success) {
          const apiUser = data.data.user || data.data;
          setUserData({
            firstName: apiUser.firstName || apiUser.first_name || '',
            lastName: apiUser.lastName || apiUser.last_name || '',
            email: apiUser.email || '',
          });
        }
      } catch (error) {
        console.error('Error loading current user:', error);
      }
    };

    loadCurrentUser();
  }, []);

  const creationOptions = [
    {
      id: 'chama',
      name: 'Chama',
      icon: 'people',
      description: 'Traditional savings and investment group with regular contributions',
      color: colors.primary
    },
    {
      id: 'contribution',
      name: 'Contribution Group',
      icon: 'heart',
      description: 'Fundraising group for specific causes and emergencies',
      color: colors.success
    },
  ];

  const chamaTypes = [
    { id: 'savings', name: 'Savings Group', icon: 'wallet', description: 'Focus on saving money together' },
    { id: 'investment', name: 'Investment Club', icon: 'trending-up', description: 'Pool funds for investments' },
    { id: 'welfare', name: 'Welfare Group', icon: 'heart', description: 'Support members in times of need' },
    { id: 'business', name: 'Business Group', icon: 'briefcase', description: 'Support business ventures' },
    { id: 'merry-go-round', name: 'Merry-go-round', icon: 'refresh', description: 'Rotating savings scheme' },
  ];

  const contributionTypes = [
    { id: 'emergency', name: 'Emergency Fund', icon: 'alert-circle', description: 'For urgent financial needs' },
    { id: 'medical', name: 'Medical Support', icon: 'medical', description: 'Healthcare and medical expenses' },
    { id: 'education', name: 'Education Fund', icon: 'school', description: 'School fees and educational support' },
    { id: 'community', name: 'Community Project', icon: 'people', description: 'Local community development' },
    { id: 'personal', name: 'Personal Goal', icon: 'person', description: 'Individual financial goals' },
  ];

  const frequencies = [
    { id: 'weekly', name: 'Weekly' },
    { id: 'monthly', name: 'Monthly' },
    { id: 'quarterly', name: 'Quarterly' },
  ];

  const memberRoles = [
    { id: 'member', name: 'Member', description: 'Regular member with basic privileges' },
    { id: 'treasurer', name: 'Treasurer', description: 'Manages finances and transactions' },
    { id: 'secretary', name: 'Secretary', description: 'Keeps records and manages communications' },
  ];

  const handleInputChange = (field: string, value: any) => {
    setChamaData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateStep = (step: number): boolean => {
    const errors: {[key: string]: string} = {};

    switch (step) {
      case 1:
        if (!chamaData.group_type) {
          errors.group_type = 'Please select what you want to create';
        }
        if (!chamaData.name.trim()) {
          errors.name = 'Name is required';
        }
        if (!chamaData.description.trim()) {
          errors.description = 'Description is required';
        }
        if (!chamaData.type) {
          errors.type = 'Please select a type';
        }
        break;

      case 2:
        if (!chamaData.county.trim()) {
          errors.county = 'County is required';
        }
        if (!chamaData.town.trim()) {
          errors.town = 'Town is required';
        }
        if (!chamaData.max_members) {
          errors.max_members = 'Maximum members is required';
        }

        if (chamaData.group_type === 'chama' && !chamaData.contribution_amount) {
          errors.contribution_amount = 'Contribution amount is required';
        }
        if (chamaData.group_type === 'contribution' && !chamaData.target_amount) {
          errors.target_amount = 'Target amount is required';
        }
        break;
    }

    setFormErrors(errors);
    setShowErrors(Object.keys(errors).length > 0);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
      setShowErrors(false);
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    try {
      setLoading(true);

      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        alert('Authentication required. Please login again.');
        window.location.href = '/login';
        return;
      }

      // Prepare data for API submission
      const submitData: any = {
        category: chamaData.group_type,
        name: chamaData.name,
        description: chamaData.description,
        type: chamaData.type,
        county: chamaData.county,
        town: chamaData.town,
        max_members: parseInt(chamaData.max_members),
        is_public: chamaData.is_public,
        requires_approval: chamaData.requires_approval,
      };

      // Add chama-specific fields
      if (chamaData.group_type === 'chama') {
        submitData.contribution_amount = parseFloat(chamaData.contribution_amount);
        submitData.contribution_frequency = chamaData.contribution_frequency;
        submitData.rules = chamaData.rules;
      }

      // Add contribution-specific fields
      if (chamaData.group_type === 'contribution') {
        submitData.target_amount = parseFloat(chamaData.target_amount);
        submitData.contribution_rules = chamaData.contribution_rules;
      }

      // Add payment method fields if provided
      if (chamaData.payment_method) {
        submitData.payment_method = chamaData.payment_method;
        if (chamaData.payment_method === 'till') {
          submitData.till_number = chamaData.till_number;
        } else if (chamaData.payment_method === 'paybill') {
          submitData.paybill_business_number = chamaData.paybill_business_number;
          submitData.paybill_account_number = chamaData.paybill_account_number;
        }
        submitData.payment_recipient_name = chamaData.payment_recipient_name;
      }

      // Add optional fields
      if (chamaData.meeting_schedule) {
        submitData.meeting_schedule = chamaData.meeting_schedule;
      }

      // Add selected members if any
      if (selectedMembers.length > 0) {
        submitData.members = selectedMembers.map(member => ({
          user_id: member.id,
          role: member.role,
        }));
      }

      // Always include creator as chairperson
      submitData.members = submitData.members || [];
      // Don't add current user as chairperson here - backend handles this automatically

      const response = await fetch(API_ENDPOINTS.CREATE_CHAMA, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData),
      });

      const data = await response.json();

      if (data.success) {
        alert(`${chamaData.group_type === 'contribution' ? 'Contribution Group' : 'Chama'} created successfully!`);
        // Navigate back to MyChamas
        window.location.href = '/dashboard/mychamas';
      } else {
        alert(data.error || `Failed to create ${chamaData.group_type === 'contribution' ? 'contribution group' : 'chama'}. Please try again.`);
      }
    } catch (error) {
      console.error('Failed to create chama:', error);
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        alert('Network error. Please check your connection and try again.');
      } else {
        alert('An error occurred while creating the chama. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const searchUsers = async (query: string) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setSearchLoading(true);

      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        console.error('No auth token found');
        setSearchResults([]);
        return;
      }

      console.log('Searching for users with query:', query);

      // Use the actual API endpoint for user search
      const response = await fetch(`${API_ENDPOINTS.USER_SEARCH}?query=${encodeURIComponent(query)}&excludeCurrentUser=true`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (data.success) {
        // Handle different response formats - data.data might be an array or object
        let usersArray = [];
        if (Array.isArray(data.data)) {
          usersArray = data.data;
        } else if (data.data && typeof data.data === 'object') {
          // If it's an object with users property, use that
          usersArray = data.data.users || data.data.items || data.data || [];
        }

        // Transform API response to match our Member interface
        const transformedUsers: Member[] = usersArray.map((user: any) => ({
          id: user.id,
          firstName: user.firstName || user.first_name || '',
          lastName: user.lastName || user.last_name || '',
          email: user.email || '',
          phone: user.phone || user.phoneNumber || '',
          role: 'member', // Default role for new members
        }));
        setSearchResults(transformedUsers);
      } else {
        console.error('Search failed:', data.error);
        setSearchResults([]);
      }

    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const addMember = (user: Member) => {
    setSelectedMembers(prev => [...prev, { ...user, role: 'member' }]);
    setSearchResults(prev => prev.filter(u => u.id !== user.id));
    setSearchQuery('');
  };

  const removeMember = (userId: string) => {
    setSelectedMembers(prev => prev.filter(member => member.id !== userId));
  };

  const updateMemberRole = (userId: string, newRole: string) => {
    setSelectedMembers(prev =>
      prev.map(member =>
        member.id === userId ? { ...member, role: newRole } : member
      )
    );
  };

  const renderStepIndicator = () => (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      paddingTop: spacing.lg,
      paddingBottom: spacing.lg,
      paddingLeft: spacing.md,
      paddingRight: spacing.md,
    }}>
      {[1, 2, 3, 4].map((step) => (
        <div key={step} style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{
            width: 32,
            height: 32,
            borderRadius: 16,
            border: `2px solid ${step <= currentStep ? colors.primary : colors.border}`,
            backgroundColor: step <= currentStep ? colors.primary : colors.backgroundSecondary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <span style={{
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.bold,
              color: step <= currentStep ? colors.white : colors.textSecondary,
            }}>
              {step}
            </span>
          </div>
          {step < 4 && (
            <div style={{
              width: 40,
              height: 2,
              marginLeft: spacing.sm,
              marginRight: spacing.sm,
              backgroundColor: step < currentStep ? colors.primary : colors.border,
            }} />
          )}
        </div>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div style={{
      backgroundColor: colors.surface,
      borderRadius: borderRadius.lg,
      padding: spacing.lg,
      marginBottom: spacing.lg,
      ...getShadowStyle('md'),
      border: `1px solid ${colors.border}`,
    }}>
      <h2 style={{
        fontSize: typography.fontSize.xl,
        fontWeight: typography.fontWeight.bold,
        color: colors.text,
        marginBottom: spacing.lg,
        textAlign: 'center',
      }}>
        What do you want to create?
      </h2>
      <p style={{
        fontSize: typography.fontSize.base,
        color: colors.textSecondary,
        marginBottom: spacing.xl,
        textAlign: 'center',
      }}>
        Choose the type of group you want to create
      </p>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: spacing.md,
        marginBottom: spacing.md,
      }}>
        {creationOptions.map((option) => (
          <div
            key={option.id}
            style={{
              padding: spacing.md,
              borderRadius: borderRadius.lg,
              border: `2px solid ${chamaData.group_type === option.id ? option.color : colors.border}`,
              backgroundColor: chamaData.group_type === option.id ? option.color + '20' : colors.backgroundSecondary,
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
            }}
            onClick={() => {
              setChamaData(prev => ({
                ...prev,
                group_type: option.id,
                type: '',
                contribution_amount: option.id === 'contribution' ? '' : prev.contribution_amount,
                target_amount: option.id === 'chama' ? '' : prev.target_amount,
                contribution_rules: option.id === 'chama' ? '' : prev.contribution_rules,
                rules: option.id === 'contribution' ? '' : prev.rules,
              }));
              setFormErrors({});
            }}
          >
            <span style={{ fontSize: 32, marginBottom: spacing.sm }}>
              {option.icon === 'people' ? '👥' : '❤️'}
            </span>
            <h3 style={{
              fontSize: typography.fontSize.lg,
              fontWeight: typography.fontWeight.bold,
              color: chamaData.group_type === option.id ? option.color : colors.text,
              marginBottom: spacing.xs,
            }}>
              {option.name}
            </h3>
            <p style={{
              fontSize: typography.fontSize.sm,
              color: colors.textSecondary,
            }}>
              {option.description}
            </p>
          </div>
        ))}
      </div>

      {chamaData.group_type && (
        <>
          <div style={{ marginBottom: spacing.lg }}>
            <label style={{
              display: 'block',
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.medium,
              color: colors.textSecondary,
              marginBottom: spacing.sm,
            }}>
              {chamaData.group_type === 'contribution' ? 'Contribution Group' : 'Chama'} Name *
            </label>
            <input
              type="text"
              value={chamaData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder={`Enter ${chamaData.group_type === 'contribution' ? 'contribution group' : 'chama'} name`}
              style={{
                width: '100%',
                padding: spacing.md,
                borderRadius: borderRadius.md,
                border: `1px solid ${formErrors.name ? colors.error : colors.border}`,
                backgroundColor: colors.background,
                color: colors.text,
                fontSize: typography.fontSize.base,
                outline: 'none',
              }}
            />
            {formErrors.name && (
              <span style={{
                fontSize: typography.fontSize.sm,
                color: colors.error,
                marginTop: spacing.xs,
                display: 'block',
              }}>
                {formErrors.name}
              </span>
            )}
          </div>

          <div style={{ marginBottom: spacing.lg }}>
            <label style={{
              display: 'block',
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.medium,
              color: colors.textSecondary,
              marginBottom: spacing.sm,
            }}>
              Description *
            </label>
            <textarea
              value={chamaData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder={`Describe your ${chamaData.group_type === 'contribution' ? 'contribution goal and purpose' : 'chama\'s purpose'}...`}
              rows={4}
              style={{
                width: '100%',
                padding: spacing.md,
                borderRadius: borderRadius.md,
                border: `1px solid ${formErrors.description ? colors.error : colors.border}`,
                backgroundColor: colors.background,
                color: colors.text,
                fontSize: typography.fontSize.base,
                outline: 'none',
                resize: 'vertical',
              }}
            />
            {formErrors.description && (
              <span style={{
                fontSize: typography.fontSize.sm,
                color: colors.error,
                marginTop: spacing.xs,
                display: 'block',
              }}>
                {formErrors.description}
              </span>
            )}
          </div>

          <div style={{ marginBottom: spacing.lg }}>
            <label style={{
              display: 'block',
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.medium,
              color: colors.textSecondary,
              marginBottom: spacing.sm,
            }}>
              {chamaData.group_type === 'contribution' ? 'Contribution Type' : 'Chama Type'} *
            </label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: spacing.sm,
            }}>
              {(chamaData.group_type === 'contribution' ? contributionTypes : chamaTypes).map((type) => (
                <div
                  key={type.id}
                  style={{
                    padding: spacing.md,
                    borderRadius: borderRadius.md,
                    border: `1px solid ${chamaData.type === type.id ? colors.primary : colors.border}`,
                    backgroundColor: chamaData.type === type.id ? colors.primary + '20' : colors.backgroundSecondary,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  onClick={() => handleInputChange('type', type.id)}
                >
                  <span style={{ marginRight: spacing.sm, fontSize: 20 }}>
                    {type.icon === 'wallet' ? '💰' :
                     type.icon === 'trending-up' ? '📈' :
                     type.icon === 'heart' ? '❤️' :
                     type.icon === 'briefcase' ? '💼' :
                     type.icon === 'refresh' ? '🔄' :
                     type.icon === 'alert-circle' ? '⚠️' :
                     type.icon === 'medical' ? '🏥' :
                     type.icon === 'school' ? '🎓' :
                     type.icon === 'people' ? '👥' :
                     type.icon === 'person' ? '👤' : '📋'}
                  </span>
                  <div>
                    <div style={{
                      fontSize: typography.fontSize.base,
                      fontWeight: typography.fontWeight.medium,
                      color: chamaData.type === type.id ? colors.primary : colors.text,
                    }}>
                      {type.name}
                    </div>
                    <div style={{
                      fontSize: typography.fontSize.sm,
                      color: colors.textSecondary,
                    }}>
                      {type.description}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {formErrors.type && (
              <span style={{
                fontSize: typography.fontSize.sm,
                color: colors.error,
                marginTop: spacing.xs,
                display: 'block',
              }}>
                {formErrors.type}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div style={{
      backgroundColor: colors.surface,
      borderRadius: borderRadius.lg,
      padding: spacing.lg,
      marginBottom: spacing.lg,
      ...getShadowStyle('md'),
      border: `1px solid ${colors.border}`,
    }}>
      <h2 style={{
        fontSize: typography.fontSize.xl,
        fontWeight: typography.fontWeight.bold,
        color: colors.text,
        marginBottom: spacing.lg,
        textAlign: 'center',
      }}>
        {chamaData.group_type === 'contribution' ? 'Target & Location Details' : 'Financial & Location Details'}
      </h2>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: spacing.md,
        marginBottom: spacing.lg,
      }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: typography.fontSize.sm,
            fontWeight: typography.fontWeight.medium,
            color: colors.textSecondary,
            marginBottom: spacing.sm,
          }}>
            County *
          </label>
          <input
            type="text"
            value={chamaData.county}
            onChange={(e) => handleInputChange('county', e.target.value)}
            placeholder="Select county"
            style={{
              width: '100%',
              padding: spacing.md,
              borderRadius: borderRadius.md,
              border: `1px solid ${formErrors.county ? colors.error : colors.border}`,
              backgroundColor: colors.background,
              color: colors.text,
              fontSize: typography.fontSize.base,
              outline: 'none',
            }}
          />
          {formErrors.county && (
            <span style={{
              fontSize: typography.fontSize.sm,
              color: colors.error,
              marginTop: spacing.xs,
              display: 'block',
            }}>
              {formErrors.county}
            </span>
          )}
        </div>

        <div>
          <label style={{
            display: 'block',
            fontSize: typography.fontSize.sm,
            fontWeight: typography.fontWeight.medium,
            color: colors.textSecondary,
            marginBottom: spacing.sm,
          }}>
            Town *
          </label>
          <input
            type="text"
            value={chamaData.town}
            onChange={(e) => handleInputChange('town', e.target.value)}
            placeholder="Select town"
            style={{
              width: '100%',
              padding: spacing.md,
              borderRadius: borderRadius.md,
              border: `1px solid ${formErrors.town ? colors.error : colors.border}`,
              backgroundColor: colors.background,
              color: colors.text,
              fontSize: typography.fontSize.base,
              outline: 'none',
            }}
          />
          {formErrors.town && (
            <span style={{
              fontSize: typography.fontSize.sm,
              color: colors.error,
              marginTop: spacing.xs,
              display: 'block',
            }}>
              {formErrors.town}
            </span>
          )}
        </div>
      </div>

      {chamaData.group_type === 'chama' && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: spacing.md,
          marginBottom: spacing.lg,
        }}>
          <div>
            <label style={{
              display: 'block',
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.medium,
              color: colors.textSecondary,
              marginBottom: spacing.sm,
            }}>
              Contribution Amount (KES) *
            </label>
            <input
              type="number"
              value={chamaData.contribution_amount}
              onChange={(e) => handleInputChange('contribution_amount', e.target.value)}
              placeholder="0"
              style={{
                width: '100%',
                padding: spacing.md,
                borderRadius: borderRadius.md,
                border: `1px solid ${formErrors.contribution_amount ? colors.error : colors.border}`,
                backgroundColor: colors.background,
                color: colors.text,
                fontSize: typography.fontSize.base,
                outline: 'none',
              }}
            />
            {formErrors.contribution_amount && (
              <span style={{
                fontSize: typography.fontSize.sm,
                color: colors.error,
                marginTop: spacing.xs,
                display: 'block',
              }}>
                {formErrors.contribution_amount}
              </span>
            )}
          </div>

          <div>
            <label style={{
              display: 'block',
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.medium,
              color: colors.textSecondary,
              marginBottom: spacing.sm,
            }}>
              Frequency *
            </label>
            <div style={{
              display: 'flex',
              gap: spacing.sm,
            }}>
              {frequencies.map((freq) => (
                <button
                  key={freq.id}
                  type="button"
                  style={{
                    flex: 1,
                    padding: spacing.md,
                    borderRadius: borderRadius.md,
                    border: `1px solid ${chamaData.contribution_frequency === freq.id ? colors.primary : colors.border}`,
                    backgroundColor: chamaData.contribution_frequency === freq.id ? colors.primary : colors.backgroundSecondary,
                    color: chamaData.contribution_frequency === freq.id ? colors.white : colors.text,
                    fontSize: typography.fontSize.sm,
                    fontWeight: typography.fontWeight.medium,
                    cursor: 'pointer',
                    outline: 'none',
                  }}
                  onClick={() => handleInputChange('contribution_frequency', freq.id)}
                >
                  {freq.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {chamaData.group_type === 'contribution' && (
        <>
          <div style={{ marginBottom: spacing.lg }}>
            <label style={{
              display: 'block',
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.medium,
              color: colors.textSecondary,
              marginBottom: spacing.sm,
            }}>
              Target Amount (KES) *
            </label>
            <input
              type="number"
              value={chamaData.target_amount}
              onChange={(e) => handleInputChange('target_amount', e.target.value)}
              placeholder="e.g., 50000"
              style={{
                width: '100%',
                padding: spacing.md,
                borderRadius: borderRadius.md,
                border: `1px solid ${formErrors.target_amount ? colors.error : colors.border}`,
                backgroundColor: colors.background,
                color: colors.text,
                fontSize: typography.fontSize.base,
                outline: 'none',
              }}
            />
            {formErrors.target_amount && (
              <span style={{
                fontSize: typography.fontSize.sm,
                color: colors.error,
                marginTop: spacing.xs,
                display: 'block',
              }}>
                {formErrors.target_amount}
              </span>
            )}
          </div>

          <div style={{ marginBottom: spacing.lg }}>
            <label style={{
              display: 'block',
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.medium,
              color: colors.textSecondary,
              marginBottom: spacing.sm,
            }}>
              Contribution Rules
            </label>
            <textarea
              value={chamaData.contribution_rules}
              onChange={(e) => handleInputChange('contribution_rules', e.target.value)}
              placeholder="e.g., Minimum KES 100 per person, Deadline: End of month, No refunds after target reached"
              rows={3}
              style={{
                width: '100%',
                padding: spacing.md,
                borderRadius: borderRadius.md,
                border: `1px solid ${colors.border}`,
                backgroundColor: colors.background,
                color: colors.text,
                fontSize: typography.fontSize.base,
                outline: 'none',
                resize: 'vertical',
              }}
            />
          </div>
        </>
      )}

      <div style={{ marginBottom: spacing.lg }}>
        <label style={{
          display: 'block',
          fontSize: typography.fontSize.sm,
          fontWeight: typography.fontWeight.medium,
          color: colors.textSecondary,
          marginBottom: spacing.sm,
        }}>
          Maximum Members *
        </label>
        <input
          type="number"
          value={chamaData.max_members}
          onChange={(e) => handleInputChange('max_members', e.target.value)}
          placeholder="e.g., 20"
          style={{
            width: '100%',
            padding: spacing.md,
            borderRadius: borderRadius.md,
            border: `1px solid ${formErrors.max_members ? colors.error : colors.border}`,
            backgroundColor: colors.background,
            color: colors.text,
            fontSize: typography.fontSize.base,
            outline: 'none',
          }}
        />
        {formErrors.max_members && (
          <span style={{
            fontSize: typography.fontSize.sm,
            color: colors.error,
            marginTop: spacing.xs,
            display: 'block',
          }}>
            {formErrors.max_members}
          </span>
        )}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div style={{
      backgroundColor: colors.surface,
      borderRadius: borderRadius.lg,
      padding: spacing.lg,
      marginBottom: spacing.lg,
      ...getShadowStyle('md'),
      border: `1px solid ${colors.border}`,
    }}>
      <h2 style={{
        fontSize: typography.fontSize.xl,
        fontWeight: typography.fontWeight.bold,
        color: colors.text,
        marginBottom: spacing.lg,
        textAlign: 'center',
      }}>
        Add Members (Optional)
      </h2>

      <div style={{ marginBottom: spacing.lg }}>
        <label style={{
          display: 'block',
          fontSize: typography.fontSize.sm,
          fontWeight: typography.fontWeight.medium,
          color: colors.textSecondary,
          marginBottom: spacing.sm,
        }}>
          Search Users
        </label>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            searchUsers(e.target.value);
          }}
          placeholder="Search by name or email..."
          style={{
            width: '100%',
            padding: spacing.md,
            borderRadius: borderRadius.md,
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.background,
            color: colors.text,
            fontSize: typography.fontSize.base,
            outline: 'none',
          }}
        />
      </div>

      {searchResults.length > 0 && (
        <div style={{ marginBottom: spacing.lg }}>
          <h3 style={{
            fontSize: typography.fontSize.lg,
            fontWeight: typography.fontWeight.semibold,
            color: colors.text,
            marginBottom: spacing.md,
          }}>
            Search Results
          </h3>
          {searchResults.map((user) => (
            <div
              key={user.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: spacing.md,
                borderRadius: borderRadius.md,
                backgroundColor: colors.backgroundSecondary,
                marginBottom: spacing.sm,
                border: `1px solid ${colors.border}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <div style={{
                  width: 40,
                  height: 40,
                  borderRadius: 20,
                  backgroundColor: colors.primary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: spacing.md,
                }}>
                  <span style={{
                    fontSize: typography.fontSize.base,
                    fontWeight: typography.fontWeight.bold,
                    color: colors.white,
                  }}>
                    {user.firstName?.[0]}{user.lastName?.[0]}
                  </span>
                </div>
                <div>
                  <div style={{
                    fontSize: typography.fontSize.base,
                    fontWeight: typography.fontWeight.medium,
                    color: colors.text,
                  }}>
                    {user.firstName} {user.lastName}
                  </div>
                  <div style={{
                    fontSize: typography.fontSize.sm,
                    color: colors.textSecondary,
                  }}>
                    {user.email}
                  </div>
                </div>
              </div>
              <button
                onClick={() => addMember(user)}
                style={{
                  padding: `${spacing.sm} ${spacing.md}`,
                  borderRadius: borderRadius.md,
                  backgroundColor: colors.primary,
                  color: colors.white,
                  border: 'none',
                  fontSize: typography.fontSize.sm,
                  fontWeight: typography.fontWeight.medium,
                  cursor: 'pointer',
                  outline: 'none',
                }}
              >
                Add
              </button>
            </div>
          ))}
        </div>
      )}

      <div>
        <h3 style={{
          fontSize: typography.fontSize.lg,
          fontWeight: typography.fontWeight.semibold,
          color: colors.text,
          marginBottom: spacing.md,
        }}>
          Selected Members ({selectedMembers.length + 1})
        </h3>

        {/* Creator (Chairperson) */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: spacing.md,
          borderRadius: borderRadius.md,
          backgroundColor: colors.secondary + '20',
          marginBottom: spacing.sm,
          border: `1px solid ${colors.secondary}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <div style={{
              width: 40,
              height: 40,
              borderRadius: 20,
              backgroundColor: colors.secondary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: spacing.md,
            }}>
              <span style={{
                fontSize: typography.fontSize.base,
                fontWeight: typography.fontWeight.bold,
                color: colors.white,
              }}>
                {userData.firstName?.[0]}{userData.lastName?.[0]}
              </span>
            </div>
            <div>
              <div style={{
                fontSize: typography.fontSize.base,
                fontWeight: typography.fontWeight.medium,
                color: colors.text,
              }}>
                {userData.firstName} {userData.lastName} (Chairperson)
              </div>
              <div style={{
                fontSize: typography.fontSize.sm,
                color: colors.textSecondary,
              }}>
                {userData.email}
              </div>
            </div>
          </div>
          <div style={{
            padding: `${spacing.xs} ${spacing.sm}`,
            borderRadius: borderRadius.md,
            backgroundColor: colors.secondary,
            color: colors.white,
            fontSize: typography.fontSize.sm,
            fontWeight: typography.fontWeight.medium,
          }}>
            Chairperson
          </div>
        </div>

        {/* Other Members */}
        {selectedMembers.map((member) => (
          <div
            key={member.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: spacing.md,
              borderRadius: borderRadius.md,
              backgroundColor: colors.backgroundSecondary,
              marginBottom: spacing.sm,
              border: `1px solid ${colors.border}`,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <div style={{
                width: 40,
                height: 40,
                borderRadius: 20,
                backgroundColor: colors.primary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: spacing.md,
              }}>
                <span style={{
                  fontSize: typography.fontSize.base,
                  fontWeight: typography.fontWeight.bold,
                  color: colors.white,
                }}>
                  {member.firstName?.[0]}{member.lastName?.[0]}
                </span>
              </div>
              <div>
                <div style={{
                  fontSize: typography.fontSize.base,
                  fontWeight: typography.fontWeight.medium,
                  color: colors.text,
                }}>
                  {member.firstName} {member.lastName}
                </div>
                <div style={{
                  fontSize: typography.fontSize.sm,
                  color: colors.textSecondary,
                }}>
                  {member.email}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, flexWrap: 'wrap' }}>
              <div style={{ minWidth: 100, flex: 1 }}>
                <select
                  value={member.role}
                  onChange={(e) => updateMemberRole(member.id, e.target.value)}
                  style={{
                    width: '100%',
                    padding: `${spacing.sm} ${spacing.md}`,
                    borderRadius: borderRadius.md,
                    border: `1px solid ${colors.border}`,
                    backgroundColor: colors.background,
                    color: colors.text,
                    fontSize: typography.fontSize.sm,
                    fontWeight: typography.fontWeight.medium,
                    cursor: 'pointer',
                    outline: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    appearance: 'none',
                    backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='${colors.textSecondary.replace('#', '%23')}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 8px center',
                    backgroundSize: '16px',
                    paddingRight: 40,
                  }}
                >
                  {memberRoles.map((role) => (
                    <option key={role.id} value={role.id} style={{ backgroundColor: colors.background, color: colors.text }}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </div>

              <button
                onClick={() => removeMember(member.id)}
                style={{
                  padding: spacing.xs,
                  borderRadius: borderRadius.sm,
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: colors.error,
                  cursor: 'pointer',
                  outline: 'none',
                }}
              >
                ✕
              </button>
            </div>
          </div>
        ))}
      </div>

      {selectedMembers.length === 0 && searchResults.length === 0 && !searchQuery && (
        <div style={{
          textAlign: 'center',
          padding: spacing.xl,
        }}>
          <div style={{
            fontSize: 48,
            color: colors.textTertiary,
            marginBottom: spacing.lg,
          }}>
            👥
          </div>
          <div style={{
            fontSize: typography.fontSize.base,
            fontWeight: typography.fontWeight.medium,
            color: colors.textTertiary,
            marginBottom: spacing.sm,
          }}>
            Search for users to add as members
          </div>
          <div style={{
            fontSize: typography.fontSize.sm,
            color: colors.textTertiary,
          }}>
            You can skip this step and add members later
          </div>
        </div>
      )}
    </div>
  );

  const renderStep4 = () => (
    <div style={{
      backgroundColor: colors.surface,
      borderRadius: borderRadius.lg,
      padding: spacing.lg,
      marginBottom: spacing.lg,
      ...getShadowStyle('md'),
      border: `1px solid ${colors.border}`,
    }}>
      <h2 style={{
        fontSize: typography.fontSize.xl,
        fontWeight: typography.fontWeight.bold,
        color: colors.text,
        marginBottom: spacing.lg,
        textAlign: 'center',
      }}>
        Settings & Rules
      </h2>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: spacing.md,
        borderRadius: borderRadius.md,
        backgroundColor: colors.backgroundSecondary,
        marginBottom: spacing.md,
        border: `1px solid ${colors.border}`,
      }}>
        <div>
          <div style={{
            fontSize: typography.fontSize.base,
            fontWeight: typography.fontWeight.medium,
            color: colors.text,
            marginBottom: spacing.xs,
          }}>
            {chamaData.group_type === 'contribution' ? 'Public Contribution Group' : 'Public Chama'}
          </div>
          <div style={{
            fontSize: typography.fontSize.sm,
            color: colors.textSecondary,
          }}>
            {chamaData.group_type === 'contribution'
              ? 'Allow others to discover and join your contribution group'
              : 'Allow others to discover and join your chama'
            }
          </div>
        </div>
        <label style={{ position: 'relative', display: 'inline-block', width: 50, height: 24 }}>
          <input
            type="checkbox"
            checked={chamaData.is_public}
            onChange={(e) => handleInputChange('is_public', e.target.checked)}
            style={{ opacity: 0, width: 0, height: 0 }}
          />
          <span style={{
            position: 'absolute',
            cursor: 'pointer',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: chamaData.is_public ? colors.primary : colors.border,
            borderRadius: 24,
            transition: '0.4s',
          }}>
            <span style={{
              position: 'absolute',
              height: 18,
              width: 18,
              left: chamaData.is_public ? 28 : 3,
              bottom: 3,
              backgroundColor: colors.white,
              borderRadius: 50,
              transition: '0.4s',
            }} />
          </span>
        </label>
      </div>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: spacing.md,
        borderRadius: borderRadius.md,
        backgroundColor: colors.backgroundSecondary,
        marginBottom: spacing.md,
        border: `1px solid ${colors.border}`,
      }}>
        <div>
          <div style={{
            fontSize: typography.fontSize.base,
            fontWeight: typography.fontWeight.medium,
            color: colors.text,
            marginBottom: spacing.xs,
          }}>
            Require Approval
          </div>
          <div style={{
            fontSize: typography.fontSize.sm,
            color: colors.textSecondary,
          }}>
            New members need approval to join
          </div>
        </div>
        <label style={{ position: 'relative', display: 'inline-block', width: 50, height: 24 }}>
          <input
            type="checkbox"
            checked={chamaData.requires_approval}
            onChange={(e) => handleInputChange('requires_approval', e.target.checked)}
            style={{ opacity: 0, width: 0, height: 0 }}
          />
          <span style={{
            position: 'absolute',
            cursor: 'pointer',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: chamaData.requires_approval ? colors.primary : colors.border,
            borderRadius: 24,
            transition: '0.4s',
          }}>
            <span style={{
              position: 'absolute',
              height: 18,
              width: 18,
              left: chamaData.requires_approval ? 28 : 3,
              bottom: 3,
              backgroundColor: colors.white,
              borderRadius: 50,
              transition: '0.4s',
            }} />
          </span>
        </label>
      </div>

      {chamaData.group_type === 'chama' && (
        <div style={{ marginBottom: spacing.lg }}>
          <label style={{
            display: 'block',
            fontSize: typography.fontSize.sm,
            fontWeight: typography.fontWeight.medium,
            color: colors.textSecondary,
            marginBottom: spacing.sm,
          }}>
            Chama Rules (Optional)
          </label>
          <textarea
            value={chamaData.rules}
            onChange={(e) => handleInputChange('rules', e.target.value)}
            placeholder="Define rules and guidelines for your chama..."
            rows={4}
            style={{
              width: '100%',
              padding: spacing.md,
              borderRadius: borderRadius.md,
              border: `1px solid ${colors.border}`,
              backgroundColor: colors.background,
              color: colors.text,
              fontSize: typography.fontSize.base,
              outline: 'none',
              resize: 'vertical',
            }}
          />
        </div>
      )}

      <div style={{ marginBottom: spacing.lg }}>
        <label style={{
          display: 'block',
          fontSize: typography.fontSize.sm,
          fontWeight: typography.fontWeight.medium,
          color: colors.textSecondary,
          marginBottom: spacing.sm,
        }}>
          Meeting Schedule (Optional)
        </label>
        <input
          type="text"
          value={chamaData.meeting_schedule}
          onChange={(e) => handleInputChange('meeting_schedule', e.target.value)}
          placeholder={chamaData.group_type === 'contribution'
            ? 'e.g., Weekly check-ins every Sunday at 7 PM'
            : 'e.g., Every first Saturday of the month at 2 PM'
          }
          style={{
            width: '100%',
            padding: spacing.md,
            borderRadius: borderRadius.md,
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.background,
            color: colors.text,
            fontSize: typography.fontSize.base,
            outline: 'none',
          }}
        />
      </div>
    </div>
  );

  const renderNavigationButtons = () => (
    <div style={{
      position: 'fixed',
      bottom: 80, // Position above the tab bar (assuming tab bar is 80px high)
      left: 0,
      right: 0,
      backgroundColor: colors.surface,
      padding: spacing.md,
      borderTop: `1px solid ${colors.border}`,
      display: 'flex',
      gap: spacing.md,
      zIndex: 1000,
      boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)',
    }}>
      {currentStep > 1 && (
        <button
          onClick={handleBack}
          style={{
            flex: 1,
            padding: spacing.md,
            borderRadius: borderRadius.md,
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.backgroundSecondary,
            color: colors.text,
            fontSize: typography.fontSize.base,
            fontWeight: typography.fontWeight.medium,
            cursor: 'pointer',
            outline: 'none',
          }}
        >
          Back
        </button>
      )}

      {currentStep < 4 ? (
        <button
          onClick={handleNext}
          style={{
            flex: 1,
            padding: spacing.md,
            borderRadius: borderRadius.md,
            backgroundColor: colors.primary,
            color: colors.white,
            border: 'none',
            fontSize: typography.fontSize.base,
            fontWeight: typography.fontWeight.medium,
            cursor: 'pointer',
            outline: 'none',
          }}
        >
          Next
        </button>
      ) : (
        <button
          onClick={handleSubmit}
          disabled={loading}
          style={{
            flex: 1,
            padding: spacing.md,
            borderRadius: borderRadius.md,
            backgroundColor: loading ? colors.border : colors.primary,
            color: colors.white,
            border: 'none',
            fontSize: typography.fontSize.base,
            fontWeight: typography.fontWeight.medium,
            cursor: loading ? 'not-allowed' : 'pointer',
            outline: 'none',
            opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? 'Creating...' : `Create ${chamaData.group_type === 'contribution' ? 'Contribution Group' : 'Chama'}`}
        </button>
      )}
    </div>
  );

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      minHeight: '100vh',
      backgroundColor: colors.background,
      paddingBottom: 80, // Space for tab bar
    },
    scrollableContent: {
      flex: 1,
      overflowY: 'auto',
      paddingTop: 80, // Space for fixed header
      paddingBottom: 80, // Space for fixed tab bar
    },
    content: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: spacing.md,
    },
  };

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="Create New Chama"
        subtitle="Set up your chama group and start managing contributions"
        showBackButton={false}
        showHomeButton={false}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
          {renderStepIndicator()}

          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
        </div>
      </div>

      {renderNavigationButtons()}
    </div>
  );
};

export default CreateChama;